import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Users, Check, Plus, Lock, Crown, ChevronRight, Clock, Upload, X } from 'lucide-react';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import usePushNotifications from '@/hooks/usePushNotifications';

const PRESET_CATEGORIES = [
  'Sports & Fitness',
  'Food & Drinks',
  'Arts & Entertainment',
  'Travel & Adventure',
  'Social & Lifestyle',
  'Professional',
  'Family & Kids',
];

const GROUP_CATEGORIES = ['All', ...PRESET_CATEGORIES];

const Groups = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [myPrivateGroups, setMyPrivateGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [canCreatePrivate, setCanCreatePrivate] = useState(false);
  const headerRef = useRef(null);

  // Cache list state across route navigation so back from a group detail
  // lands the user instantly on the same list at the header.
  useListStatePersistence({
    key: 'groups',
    state: { groups, myGroups, myPrivateGroups, selectedCategory },
    enabled: !loading && (groups.length > 0 || myGroups.length > 0),
    onRestore: (cached) => {
      if (Array.isArray(cached.groups)) setGroups(cached.groups);
      if (Array.isArray(cached.myGroups)) setMyGroups(cached.myGroups);
      if (Array.isArray(cached.myPrivateGroups)) setMyPrivateGroups(cached.myPrivateGroups);
      if (cached.selectedCategory) setSelectedCategory(cached.selectedCategory);
      setLoading(false);
      requestAnimationFrame(() => {
        headerRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    },
  });
  
  // Create group dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Members dialog
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [membersData, setMembersData] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersGroupName, setMembersGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '🔒',
    category: 'Social & Lifestyle',
    is_private: true
  });
  
  // Custom category suggestions (free-text + autocomplete)
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  
  // Icon file upload (deferred — uploaded after group is created)
  const [pendingIconFile, setPendingIconFile] = useState(null);
  const [pendingIconPreview, setPendingIconPreview] = useState(null);
  const iconInputRef = useRef(null);
  
  // Push notifications
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribeToPush } = usePushNotifications();
  
  // Permission request state
  const [permissionStatus, setPermissionStatus] = useState('none'); // 'none', 'pending', 'granted'
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  useEffect(() => {
    fetchGroups();
    fetchCategorySuggestions();
    if (user) {
      fetchMyGroups();
      checkPrivateGroupPermission();
    }
  }, [user]);

  const fetchCategorySuggestions = async () => {
    try {
      const res = await axios.get(`${API}/groups/custom-categories`);
      setCategorySuggestions(res.data.suggestions || []);
    } catch (err) {
      // Non-blocking
    }
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Icon image must be under 5MB');
      return;
    }
    setPendingIconFile(file);
    setPendingIconPreview(URL.createObjectURL(file));
  };

  const clearPendingIcon = () => {
    setPendingIconFile(null);
    if (pendingIconPreview) URL.revokeObjectURL(pendingIconPreview);
    setPendingIconPreview(null);
    if (iconInputRef.current) iconInputRef.current.value = '';
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      // Filter out private groups for the main list
      setGroups(response.data.filter(g => !g.is_private).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups/my-groups`);
      const allMyGroups = response.data;
      setMyGroups(allMyGroups.filter(g => !g.is_private).map(g => g.slug));
      setMyPrivateGroups(allMyGroups.filter(g => g.is_private));
    } catch (error) {
      console.error('Failed to fetch my groups');
    }
  };

  const checkPrivateGroupPermission = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      const canCreate = response.data.can_create_private_groups || response.data.is_admin;
      setCanCreatePrivate(canCreate);
      
      if (canCreate) {
        setPermissionStatus('granted');
      } else {
        // Check for pending request
        try {
          const statusRes = await axios.get(`${API}/groups/my-permission-status`);
          setPermissionStatus(statusRes.data.status);
        } catch (e) {
          setPermissionStatus('none');
        }
      }
    } catch (error) {
      console.error('Failed to check permissions');
    }
  };
  
  const handleRequestPermission = async () => {
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/groups/request-permission`, { reason: requestReason });
      toast.success('Your request has been submitted!');
      setShowRequestDialog(false);
      setRequestReason('');
      setPermissionStatus('pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleJoinLeave = async (slug, isJoined, isPrivate) => {
    if (!user) {
      toast.error('Please sign in to join groups');
      return;
    }

    // Can't join private groups directly
    if (isPrivate && !isJoined) {
      toast.error('This is a private group. You need an invitation to join.');
      return;
    }

    try {
      if (isJoined) {
        await axios.post(`${API}/groups/${slug}/leave`);
        setMyGroups(prev => prev.filter(g => g !== slug));
        toast.success('Left group');
      } else {
        await axios.post(`${API}/groups/${slug}/join`);
        setMyGroups(prev => [...prev, slug]);
        toast.success('Joined group!');
        
        // Ask for push notification permission when joining a group
        if (pushSupported && !pushSubscribed) {
          const subscribed = await subscribeToPush();
          if (subscribed) {
            toast.success('You\'ll be notified about new events in this group!', { icon: '🔔' });
          }
        }
      }
      fetchGroups(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group membership');
    }
  };

  const handleViewMembers = async (e, slug, groupName) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please sign in to view members');
      return;
    }
    setMembersGroupName(groupName);
    setShowMembersDialog(true);
    setMembersLoading(true);
    setMembersData(null);
    try {
      const { data } = await axios.get(`${API}/groups/${slug}/members-by-city`);
      setMembersData(data);
    } catch (err) {
      toast.error('Failed to load members');
      setShowMembersDialog(false);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    setCreating(true);
    try {
      // Generate slug from name
      const slug = newGroup.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const response = await axios.post(`${API}/groups`, {
        ...newGroup,
        slug
      });
      const newSlug = response.data.slug;

      // Upload icon if user picked one
      if (pendingIconFile) {
        try {
          const formData = new FormData();
          formData.append('file', pendingIconFile);
          await axios.post(`${API}/groups/${newSlug}/upload-icon`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          console.error('Icon upload failed:', err);
          toast.warning('Group created, but icon upload failed. You can retry from Edit Group.');
        }
      }

      toast.success('Private group created!');
      setShowCreateDialog(false);
      setNewGroup({
        name: '',
        slug: '',
        description: '',
        icon: '🔒',
        category: 'Social & Lifestyle',
        is_private: true
      });
      clearPendingIcon();
      
      // Navigate to the new group
      navigate(`/groups/${newSlug}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const filteredGroups = selectedCategory === 'All' 
    ? groups 
    : groups.filter(g => g.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3 flex items-center justify-center gap-3" ref={headerRef}>
                <svg className="w-9 h-9 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                Interest Groups
              </h1>
              <p className="text-base text-muted-foreground max-w-lg mx-auto">
                Find your people. Join groups that match your passions and never miss an event.
              </p>
            </div>

            {/* My Private Groups Section */}
            {user && myPrivateGroups.length > 0 && (
              <div className="mb-10">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-purple-600" />
                  My Private Groups
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myPrivateGroups.map((group) => (
                    <motion.div
                      key={group.group_id || group.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-br from-purple-50 to-amber-50 rounded-2xl border-2 border-purple-100 p-5 hover:shadow-lg transition-all cursor-pointer"
                      onClick={() => navigate(`/groups/${group.slug}`)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        {group.icon_url ? (
                          <img src={group.icon_url} alt={group.name} className="w-12 h-12 rounded-xl object-cover" />
                        ) : (
                          <div className="text-3xl">{group.icon || '🔒'}</div>
                        )}
                        <div className="flex items-center gap-2">
                          {group.is_admin && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <Crown className="w-3 h-3" />
                              Admin
                            </span>
                          )}
                          <Lock className="w-4 h-4 text-purple-400" />
                        </div>
                      </div>
                      <h3 className="text-lg font-bold mb-1">{group.name}</h3>
                      <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{group.description}</p>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={(e) => handleViewMembers(e, group.slug, group.name)}
                          className="text-sm text-muted-foreground hover:text-purple-600 transition-colors"
                          data-testid={`view-members-${group.slug}`}
                        >
                          <Users className="w-4 h-4 inline mr-1" />
                          {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                        </button>
                        <ChevronRight className="w-5 h-5 text-purple-400" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Create Private Group Button or Request Permission */}
            {user && canCreatePrivate && (
              <div className="flex justify-center mb-10">
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-gradient-to-r from-purple-600 to-amber-500 hover:from-purple-700 hover:to-amber-600 text-white rounded-full px-6"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Private Group
                </Button>
              </div>
            )}
            
            {user && !canCreatePrivate && permissionStatus === 'none' && (
              <div className="flex justify-center mb-10">
                <Button 
                  onClick={() => setShowRequestDialog(true)}
                  variant="outline"
                  className="rounded-full px-6 border-purple-300 text-purple-600 hover:bg-purple-50"
                >
                  <Lock className="w-5 h-5 mr-2" />
                  Request Permission to Create Private Group
                </Button>
              </div>
            )}
            
            {user && !canCreatePrivate && permissionStatus === 'pending' && (
              <div className="flex justify-center mb-10">
                <div className="bg-amber-50 border border-amber-200 rounded-full px-6 py-2 text-amber-700 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Your request is pending admin approval
                </div>
              </div>
            )}

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {GROUP_CATEGORIES.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  onClick={() => setSelectedCategory(cat)}
                  className="rounded-full"
                >
                  {cat}
                </Button>
              ))}
            </div>

            {/* Public Groups Grid */}
            <h2 className="text-xl font-bold mb-4">Public Groups</h2>
            {loading ? (
              <div className="text-center py-20">
                <div className="text-lg text-muted-foreground">Loading groups...</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredGroups.map((group, index) => {
                  const isJoined = myGroups.includes(group.slug);
                  return (
                    <motion.div
                      key={group.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="bg-white rounded-3xl border-2 border-border p-6 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        {group.icon_url ? (
                          <img src={group.icon_url} alt={group.name} className="w-14 h-14 rounded-xl object-cover" />
                        ) : (
                          <div className="text-4xl">{group.icon}</div>
                        )}
                        <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
                          {group.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-heading font-bold mb-2">{group.name}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{group.description}</p>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={(e) => handleViewMembers(e, group.slug, group.name)}
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                          data-testid={`view-members-${group.slug}`}
                        >
                          <Users className="w-4 h-4 inline mr-1" />
                          {group.member_count > 0 ? `${group.member_count} members` : 'Be first to join!'}
                        </button>
                        <Button
                          onClick={() => handleJoinLeave(group.slug, isJoined, false)}
                          variant={isJoined ? "outline" : "default"}
                          size="sm"
                          className="rounded-full"
                        >
                          {isJoined ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Joined
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-1" />
                              Join
                            </>
                          )}
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {filteredGroups.length === 0 && !loading && (
              <div className="text-center py-20">
                <p className="text-muted-foreground">No groups found in this category.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <Footer />

      {/* Create Private Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-purple-600" />
              Create Private Group
            </DialogTitle>
            <DialogDescription>
              Create an invite-only group. Only you and people you invite can see and join.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input
                placeholder="e.g., Budapest Tech Professionals"
                value={newGroup.name}
                onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                data-testid="create-group-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="What is this group about?"
                value={newGroup.description}
                onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[80px]"
                data-testid="create-group-description-input"
              />
            </div>

            {/* Icon: emoji + image upload */}
            <div className="space-y-2">
              <Label>Group Icon / Logo</Label>
              <div className="flex items-center gap-3">
                {pendingIconPreview ? (
                  <div className="relative">
                    <img
                      src={pendingIconPreview}
                      alt="Icon preview"
                      className="w-16 h-16 rounded-xl object-cover border-2 border-purple-200"
                      data-testid="create-group-icon-preview"
                    />
                    <button
                      type="button"
                      onClick={clearPendingIcon}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                      data-testid="create-group-icon-remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <Input
                    placeholder="🔒"
                    value={newGroup.icon}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, icon: e.target.value }))}
                    className="text-2xl text-center w-16 h-16"
                    data-testid="create-group-emoji-input"
                  />
                )}
                <div className="flex-1">
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleIconFileChange}
                    className="hidden"
                    data-testid="create-group-icon-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => iconInputRef.current?.click()}
                    data-testid="create-group-upload-icon-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {pendingIconPreview ? 'Change image' : 'Upload image'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use an emoji or upload an image (max 5MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Category: free-text with suggestions */}
            <div className="space-y-2">
              <Label>Category</Label>
              <Input
                list="group-category-suggestions"
                placeholder="Type a category (e.g., Crypto Investors)"
                value={newGroup.category}
                onChange={(e) => setNewGroup(prev => ({ ...prev, category: e.target.value }))}
                data-testid="create-group-category-input"
              />
              <datalist id="group-category-suggestions">
                {PRESET_CATEGORIES.map(cat => (
                  <option key={`preset-${cat}`} value={cat} />
                ))}
                {categorySuggestions
                  .filter(s => !PRESET_CATEGORIES.includes(s))
                  .map(s => (
                    <option key={`custom-${s}`} value={s} />
                  ))}
              </datalist>
              {newGroup.category && !PRESET_CATEGORIES.includes(newGroup.category.trim()) && newGroup.category.trim() && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Custom category will be reviewed by an admin.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateGroup}
              disabled={creating || !newGroup.name.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Group
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Request Permission Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-purple-600" />
              Request Permission
            </DialogTitle>
            <DialogDescription>
              Tell us why you'd like to create a private group
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Private groups allow you to create exclusive communities and invite specific members. 
              An admin will review your request.
            </p>
            
            <div>
              <Label htmlFor="reason">Why do you want to create a private group? (optional)</Label>
              <Textarea
                id="reason"
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="e.g., I want to create a community for expats from my country..."
                className="mt-2"
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestPermission}
              disabled={submittingRequest}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submittingRequest ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              {membersGroupName}
            </DialogTitle>
            <DialogDescription>
              {membersData ? (
                membersData.country && membersData.country !== 'All Countries'
                  ? `${membersData.total_in_country} member${membersData.total_in_country !== 1 ? 's' : ''} in ${membersData.country} (${membersData.total_members} total)`
                  : `${membersData.total_members} member${membersData.total_members !== 1 ? 's' : ''}`
              ) : 'Loading members...'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 max-h-[400px]">
            {membersLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : membersData?.members?.length > 0 ? (
              <div className="divide-y">
                {membersData.members.map(m => (
                  <div
                    key={m.user_id}
                    className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => { setShowMembersDialog(false); navigate(`/profile/${m.user_id}`); }}
                    data-testid={`member-${m.user_id}`}
                  >
                    {m.profile_picture ? (
                      <img src={m.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                        {m.name?.[0] || '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{m.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[m.residence_city, m.residence_country, m.nationality].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8 text-sm">
                {membersData?.country && membersData.country !== 'All Countries'
                  ? `No members from ${membersData.country} in this group`
                  : 'No members yet'}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Groups;
